import {
  getEventGoals as getEventGoalsApi,
  updateEventGoals as updateEventGoalsApi,
  deleteEventGoals as deleteEventGoalsApi,
  getKpiValues as getKpiValuesApi,
  startGoalAnalysis as startGoalAnalysisApi,
  dismissGoalGap as dismissGoalGapApi,
  restoreGoalGaps as restoreGoalGapsApi,
} from "@/lib/api/clients/EventGoalClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidEmail, isValidObjectId } from "@/lib/utils/input-validator";
// mirror of the backend AlertConfig validation constants + rules.
import { EventGoalDoc, KPI_METRICS, KpiValue } from "@/types/EventGoal";

export const MAX_GOAL_TEXT_LENGTH = 2000;
export const MAX_KPIS = 20;
export const MAX_CUSTOM_EMAILS = 20;
export const MAX_ADMIN_RECIPIENTS = 50;
export const MAX_COHORT_TARGETS = 10;
export const MAX_COHORT_LABEL_LENGTH = 120;

/** The client-writable slice of the goal doc (analysis stays server-managed). */
export type EventGoalWritable = Pick<
  EventGoalDoc,
  "goalText" | "kpis" | "cohortTargets" | "alertConfig"
>;

/** Client-side mirror of the backend PUT validation — fail fast with a code. */
export function validateEventGoalDoc(doc: EventGoalWritable): string | null {
  if ((doc.goalText ?? "").length > MAX_GOAL_TEXT_LENGTH) return "goalTextTooLong";
  if (doc.kpis.length > MAX_KPIS) return "tooManyKpis";
  for (const kpi of doc.kpis) {
    if (!KPI_METRICS.includes(kpi.metric)) return "kpiIncomplete";
    if (!Number.isFinite(kpi.target) || !Number.isFinite(kpi.thresholdValue)) return "kpiIncomplete";
    if (kpi.thresholdDirection !== "BELOW" && kpi.thresholdDirection !== "ABOVE") return "kpiIncomplete";
    // Mirror of the backend's cohort rules: a share of WHICH cohort?
    if (kpi.metric === "COHORT_SHARE") {
      if (!kpi.dimension?.trim()) return "kpiDimensionInvalid";
      const bucket = kpi.bucketValue?.trim() ?? "";
      if (bucket.length === 0 || bucket.length > MAX_COHORT_LABEL_LENGTH) return "kpiBucketValueInvalid";
      // Within a group (W3): both or neither, and never the measured value itself.
      const baseDimension = kpi.baseDimension?.trim() ?? "";
      const baseValue = kpi.baseValue?.trim() ?? "";
      if (!!baseDimension !== !!baseValue) return "kpiBaseInvalid";
      if (baseValue.length > MAX_COHORT_LABEL_LENGTH) return "kpiBaseInvalid";
      if (baseDimension && baseDimension === kpi.dimension.trim() && baseValue === bucket) return "kpiBaseInvalid";
    }
  }
  const targets = doc.cohortTargets ?? [];
  if (targets.length > MAX_COHORT_TARGETS) return "tooManyCohortTargets";
  for (const target of targets) {
    if (!target.label?.trim() || target.label.length > MAX_COHORT_LABEL_LENGTH)
      return "invalidCohortTarget";
    if ((target.dimension ?? "").length > MAX_COHORT_LABEL_LENGTH) return "invalidCohortTarget";
    if (!Number.isFinite(target.targetPercent) || target.targetPercent < 0 || target.targetPercent > 100)
      return "invalidCohortTarget";
  }
  const emails = doc.alertConfig?.customEmails ?? [];
  if (emails.length > MAX_CUSTOM_EMAILS) return "tooManyAlertEmails";
  if (emails.some((email) => !isValidEmail(email))) return "invalidAlertEmail";
  const adminIds = doc.alertConfig?.adminRecipientIds ?? [];
  if (adminIds.length > MAX_ADMIN_RECIPIENTS) return "tooManyAdminRecipients";
  if (adminIds.some((id) => !isValidObjectId(id))) return "invalidAdminRecipient";
  // No recipients at all + enabled is deliberately allowed (harmless no-op;
  // rejecting it made the immediate-commit CUSTOM radio a dead-end —
  // audit 2026-07-07). AlertConfigCard shows an inline hint instead.
  return null;
}

export async function getEventGoals(eventId: string): Promise<EventGoalDoc | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid eventId");
    return await getEventGoalsApi(eventId);
  } catch (error) {
    console.error("Error loading event goals:", error);
    return handleErrorServer(error);
  }
}

export async function updateEventGoals(
  eventId: string,
  doc: EventGoalWritable,
): Promise<EventGoalDoc | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid eventId");
    const validationError = validateEventGoalDoc(doc);
    if (validationError) throw new Error(validationError);
    return await updateEventGoalsApi(eventId, doc);
  } catch (error) {
    console.error("Error updating event goals:", error);
    return handleErrorServer(error);
  }
}

export async function deleteEventGoals(eventId: string): Promise<true | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid eventId");
    await deleteEventGoalsApi(eventId);
    return true;
  } catch (error) {
    console.error("Error deleting event goals:", error);
    return handleErrorServer(error);
  }
}

export async function getKpiValues(eventId: string): Promise<KpiValue[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid eventId");
    return await getKpiValuesApi(eventId);
  } catch (error) {
    console.error("Error loading KPI values:", error);
    return handleErrorServer(error);
  }
}

/** Persist a dismissed gap (exact persisted title — server validates against live gaps). */
export async function dismissGoalGap(
  eventId: string,
  title: string,
): Promise<true | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid eventId");
    if (!title) throw new Error("unknownGapTitle");
    await dismissGoalGapApi(eventId, title);
    return true;
  } catch (error) {
    console.error("Error dismissing goal gap:", error);
    return handleErrorServer(error);
  }
}

/** Restore every dismissed gap. */
export async function restoreGoalGaps(eventId: string): Promise<true | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid eventId");
    await restoreGoalGapsApi(eventId);
    return true;
  } catch (error) {
    console.error("Error restoring goal gaps:", error);
    return handleErrorServer(error);
  }
}

export async function startGoalAnalysis(
  eventId: string,
): Promise<{ jobId: string } | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("Invalid eventId");
    return await startGoalAnalysisApi(eventId);
  } catch (error) {
    console.error("Error starting goal analysis:", error);
    return handleErrorServer(error);
  }
}

